import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { DashboardLayout, DashboardPreset } from "./types";

interface PresetSelectorProps {
  page: string;
  defaultLayout: DashboardLayout;
  activePresetId: string | null;
  currentLayout: DashboardLayout;
  onLoad: (layout: DashboardLayout, presetId: string | null) => void;
}

export function PresetSelector({ page, defaultLayout, activePresetId, currentLayout, onLoad }: PresetSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [presets, setPresets] = useState<DashboardPreset[]>([]);
  const [saving, setSaving] = useState(false);
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [newPresetName, setNewPresetName] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch presets on mount
  useEffect(() => {
    const fetchPresets = async () => {
      try {
        const res = await fetch(`/api/presets?page=${page}`);
        const data: { success: boolean; presets?: DashboardPreset[] } = await res.json();
        if (data.success) {
          setPresets(data.presets || []);
        }
      } catch {
        // Silently fail - presets are optional
      }
    };
    fetchPresets();
  }, [page]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setShowSaveInput(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Save current layout as a new preset
  const handleSave = async () => {
    if (!newPresetName.trim()) return;
    setSaving(true);
    try {
      const res = await fetch("/api/presets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newPresetName.trim(),
          page,
          layout: currentLayout,
          isShared: false,
        }),
      });
      const data: { success: boolean; preset?: DashboardPreset } = await res.json();
      if (data.success && data.preset) {
        setPresets((prev) => [...prev, data.preset as DashboardPreset]);
        setNewPresetName("");
        setShowSaveInput(false);
        onLoad(currentLayout, data.preset._id);
      }
    } catch {
      // Silently fail
    } finally {
      setSaving(false);
    }
  };

  // Delete a user-owned preset
  const handleDelete = async (presetId: string) => {
    try {
      const res = await fetch(`/api/presets/${presetId}`, { method: "DELETE" });
      const data: { success: boolean } = await res.json();
      if (data.success) {
        setPresets((prev) => prev.filter((p) => p._id !== presetId));
        if (activePresetId === presetId) {
          onLoad(defaultLayout, null);
        }
      }
    } catch {
      // Silently fail
    }
  };

  // Separate user-owned and shared presets
  const ownPresets = presets.filter((p) => !p.isShared);
  const sharedPresets = presets.filter((p) => p.isShared);

  const activePresetName = activePresetId
    ? presets.find((p) => p._id === activePresetId)?.name || "Custom"
    : "Default";

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
        </svg>
        <span className="hidden sm:inline">{activePresetName}</span>
        <svg
          className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-72 bg-card border border-border rounded-xl shadow-xl overflow-hidden z-50"
          >
            {/* Default option */}
            <button
              onClick={() => {
                onLoad(defaultLayout, null);
                setIsOpen(false);
              }}
              className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between hover:bg-muted/50 transition-colors ${
                !activePresetId ? "bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]" : "text-foreground"
              }`}
            >
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                <span className="font-medium">Default</span>
              </div>
              {!activePresetId && (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>

            {/* User's own presets */}
            {ownPresets.length > 0 && (
              <>
                <div className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground border-t border-border bg-muted/30">
                  My Presets
                </div>
                {ownPresets.map((preset) => (
                  <div
                    key={preset._id}
                    className={`flex items-center justify-between px-4 py-2.5 hover:bg-muted/50 transition-colors ${
                      activePresetId === preset._id ? "bg-[var(--brand-primary)]/10" : ""
                    }`}
                  >
                    <button
                      onClick={() => {
                        onLoad(preset.layout, preset._id);
                        setIsOpen(false);
                      }}
                      className={`text-sm font-medium flex-1 text-left truncate ${
                        activePresetId === preset._id ? "text-[var(--brand-primary)]" : "text-foreground"
                      }`}
                    >
                      {preset.name}
                    </button>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      {activePresetId === preset._id && (
                        <svg className="w-4 h-4 text-[var(--brand-primary)]" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      {!preset.isDefault && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(preset._id);
                          }}
                          className="p-1 rounded hover:bg-red-500/10 transition-colors"
                          title="Delete preset"
                        >
                          <svg
                            className="w-3.5 h-3.5 text-muted-foreground hover:text-red-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Shared presets */}
            {sharedPresets.length > 0 && (
              <>
                <div className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground border-t border-border bg-muted/30">
                  Shared
                </div>
                {sharedPresets.map((preset) => (
                  <div
                    key={preset._id}
                    className={`flex items-center justify-between px-4 py-2.5 hover:bg-muted/50 transition-colors ${
                      activePresetId === preset._id ? "bg-[var(--brand-primary)]/10" : ""
                    }`}
                  >
                    <button
                      onClick={() => {
                        onLoad(preset.layout, preset._id);
                        setIsOpen(false);
                      }}
                      className={`text-sm font-medium flex-1 text-left truncate ${
                        activePresetId === preset._id ? "text-[var(--brand-primary)]" : "text-foreground"
                      }`}
                    >
                      {preset.name}
                      <span className="ml-1.5 text-xs text-muted-foreground">by {preset.createdByName}</span>
                    </button>
                    {activePresetId === preset._id && (
                      <svg className="w-4 h-4 text-[var(--brand-primary)] ml-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* Save current layout */}
            <div className="border-t border-border">
              <AnimatePresence mode="wait">
                {showSaveInput ? (
                  <motion.div
                    key="save-input"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="p-3 flex gap-2"
                  >
                    <input
                      autoFocus
                      value={newPresetName}
                      onChange={(e) => setNewPresetName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSave();
                        if (e.key === "Escape") {
                          setShowSaveInput(false);
                          setNewPresetName("");
                        }
                      }}
                      placeholder="Preset name..."
                      className="flex-1 px-2.5 py-1.5 text-sm rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[var(--brand-ring)]"
                    />
                    <button
                      onClick={handleSave}
                      disabled={saving || !newPresetName.trim()}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg bg-[var(--brand-primary)] text-white disabled:opacity-50 transition-opacity"
                    >
                      {saving ? "..." : "Save"}
                    </button>
                    <button
                      onClick={() => {
                        setShowSaveInput(false);
                        setNewPresetName("");
                      }}
                      className="px-2 py-1.5 text-xs font-medium rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                    >
                      Cancel
                    </button>
                  </motion.div>
                ) : (
                  <motion.button
                    key="save-button"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setShowSaveInput(true)}
                    className="w-full text-left px-4 py-2.5 text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Save Current Layout
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
